import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../App";
import { Loader2, FlaskConical, Bug, ChevronRight, Layers, Users as UsersIcon, AlertTriangle, Play, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const PHASE_LABELS = {
  phase_0_seed_locked: { title: "Phase 0 — Seed locked entries", desc: "Manual/locked entries from prior versions are placed first; subsequent phases must work around them." },
  phase_pre_leave_credits: { title: "Pre-pass — Apply leave credits", desc: "Approved leave that 'counts toward work-days' is credited to per-employee tallies so min_shifts/min_hours are satisfied without scheduling on top of leave." },
  phase_1a_minimums: { title: "Phase 1a — Place minimums", desc: "For every (date, shift) we place exactly min_staff eligible employees. This is the mandatory floor." },
  phase_1b_round_robin: { title: "Phase 1b — Round-robin extras", desc: "Add one extra employee per (date, shift) per pass up to max_staff so capacity is spread evenly across the whole roster." },
  phase_2_rebalance: { title: "Phase 2 — Rebalance min_shifts", desc: "Transfer non-locked entries from over-allocated employees to under-allocated ones until everyone hits min_shifts_per_period." },
};

const RosterDebugPage = () => {
  const [rosters, setRosters] = useState([]);
  const [loadingRosters, setLoadingRosters] = useState(true);
  const [selectedRosterId, setSelectedRosterId] = useState(null);
  const [overrideRules, setOverrideRules] = useState(false);
  const [trace, setTrace] = useState(null);
  const [running, setRunning] = useState(false);

  const loadRosters = useCallback(async () => {
    setLoadingRosters(true);
    try {
      const r = await axios.get(`${API}/admin/rosters`);
      setRosters(r.data);
    } catch {
      toast.error("Failed to load rosters");
    } finally {
      setLoadingRosters(false);
    }
  }, []);

  useEffect(() => { loadRosters(); }, [loadRosters]);

  const runTrace = async () => {
    if (!selectedRosterId) { toast.error("Pick a roster first"); return; }
    setRunning(true);
    try {
      const r = await axios.get(`${API}/admin/rosters/${selectedRosterId}/debug-trace?override_rules=${overrideRules}`);
      setTrace(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trace failed");
    } finally {
      setRunning(false);
    }
  };

  // Diff between snapshots: what was added/changed in this phase compared to the previous one
  const diffs = useMemo(() => {
    if (!trace?.snapshots) return [];
    const result = [];
    let prevEntries = [];
    for (const snap of trace.snapshots) {
      const prevKeys = new Set(prevEntries.map(e => `${e.date}|${e.shift_id}|${e.employee_id}`));
      const added = snap.entries.filter(e => !prevKeys.has(`${e.date}|${e.shift_id}|${e.employee_id}`));
      const curKeys = new Set(snap.entries.map(e => `${e.date}|${e.shift_id}|${e.employee_id}`));
      const removed = prevEntries.filter(e => !curKeys.has(`${e.date}|${e.shift_id}|${e.employee_id}`));
      result.push({ ...snap, added, removed });
      prevEntries = snap.entries;
    }
    return result;
  }, [trace]);

  const empMap = useMemo(() => {
    if (!trace?.employees) return {};
    return Object.fromEntries(trace.employees.map(e => [e.employee_id, e.name || e.employee_id]));
  }, [trace]);

  const shiftMap = useMemo(() => {
    if (!trace?.shifts) return {};
    return Object.fromEntries(trace.shifts.map(s => [s.shift_id, s.name || s.shift_id]));
  }, [trace]);

  return (
    <div className="space-y-6 max-w-6xl" data-testid="roster-debug-page">
      <div>
        <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
          <Bug className="w-5 h-5 text-[#002FA7]" /> Roster Debug Trace
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Run the deterministic engine against any roster and inspect what each phase produced.
          Use this when a generated roster looks wrong and you need to know which phase made the decision.
        </p>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wide text-zinc-400">Roster</label>
            {loadingRosters ? (
              <div className="mt-1 h-10 bg-black/40 border border-zinc-700 rounded flex items-center px-3">
                <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
              </div>
            ) : (
              <select
                value={selectedRosterId || ""}
                onChange={(e) => { setSelectedRosterId(e.target.value); setTrace(null); }}
                className="w-full mt-1 px-3 py-2 bg-black/40 border border-zinc-700 rounded text-zinc-100 text-sm"
                data-testid="roster-debug-select"
              >
                <option value="">-- pick a roster --</option>
                {rosters.map(r => {
                  const label = `${r.name} • ${r.start_date} → ${r.end_date} • ${r.user_email}`;
                  return <option key={r.roster_id} value={r.roster_id}>{label}</option>;
                })}
              </select>
            )}
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-zinc-400">Override hard rules</label>
            <label className="mt-1 flex items-center gap-2 h-10 px-3 bg-black/40 border border-zinc-700 rounded cursor-pointer">
              <input
                type="checkbox" checked={overrideRules}
                onChange={(e) => setOverrideRules(e.target.checked)}
                className="w-4 h-4 accent-[#002FA7]"
                data-testid="roster-debug-override"
              />
              <span className="text-sm text-zinc-300">Soften hard rules</span>
            </label>
          </div>
        </div>

        <button
          onClick={runTrace}
          disabled={!selectedRosterId || running}
          className="w-full py-2.5 bg-[#002FA7] hover:bg-[#00227A] text-white font-medium rounded inline-flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="roster-debug-run"
        >
          {running ? <><Loader2 className="w-4 h-4 animate-spin" /> Running engine…</> : <><Play className="w-4 h-4" /> Run trace</>}
        </button>
      </div>

      {trace && (
        <div className="space-y-4" data-testid="roster-debug-result">
          <div className="bg-zinc-900 border border-zinc-800 rounded p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-wide text-zinc-500">Final entries</p>
              <p className="text-3xl font-bold tabular-nums text-emerald-400" data-testid="trace-final-count">{trace.final_entries_count}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-zinc-500">Dates covered</p>
              <p className="text-3xl font-bold tabular-nums text-zinc-100">{trace.stats.dates_covered}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-zinc-500">Uncovered slots</p>
              <p className={`text-3xl font-bold tabular-nums ${trace.stats.uncovered_slots > 0 ? "text-amber-400" : "text-zinc-100"}`}>
                {trace.stats.uncovered_slots}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-zinc-500">Rebalance transfers</p>
              <p className="text-3xl font-bold tabular-nums text-zinc-100">{trace.stats.rebalance_applied}</p>
            </div>
          </div>

          {trace.stats.underallocated?.length > 0 && (
            <div className="bg-amber-950/30 border border-amber-800/50 rounded p-4">
              <p className="text-sm font-bold text-amber-300 inline-flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {trace.stats.underallocated.length} employee(s) under-allocated
              </p>
              <p className="text-xs text-amber-300/80 mt-1">
                Each is below their min_shifts_per_period target. The engine couldn't fit more shifts without breaking
                hard rules (min_rest, max_consecutive, max_shifts, time-off, eligibility). This is usually a sign that
                rules are too tight relative to roster window and team size.
              </p>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs tabular-nums">
                {trace.stats.underallocated.map(u => (
                  <div key={u.employee_id} className="bg-black/30 px-2 py-1.5 rounded">
                    <span className="text-zinc-300">{u.name}</span>{" "}
                    <span className="text-amber-400">{u.have}</span>
                    <span className="text-zinc-600">/{u.need}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {trace.suggestions?.length > 0 && (
            <div className="bg-emerald-950/20 border border-emerald-800/40 rounded p-5" data-testid="trace-suggestions">
              <p className="text-sm font-bold text-emerald-300 inline-flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4" /> Suggested rule relaxations
              </p>
              <p className="text-xs text-emerald-300/70 mb-4">
                The engine couldn't satisfy everyone — here's the smallest changes that would likely close the gap, ranked by impact.
                Apply the one that's least disruptive to your operation.
              </p>
              <div className="space-y-2">
                {trace.suggestions.map((s, i) => (
                  <div
                    key={`${s.rule_id || s.shift_id}-${i}`}
                    className="bg-black/30 border border-emerald-800/30 rounded p-3 flex items-start gap-3"
                    data-testid={`suggestion-${i}`}
                  >
                    <span className={`mt-0.5 flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                      s.impact === "high" ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-700/50 text-zinc-300"
                    }`}>
                      {s.impact === "high" ? "★" : "·"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-zinc-100">
                        <span className="font-mono text-emerald-300">{s.rule_type}</span>:{" "}
                        <span className="text-rose-300 line-through tabular-nums">{s.current_value}</span>
                        {" "}<ArrowRight className="inline w-3 h-3 text-zinc-500 mx-0.5" />{" "}
                        <span className="text-emerald-300 font-bold tabular-nums">{s.suggested_value}</span>
                      </p>
                      <p className="text-xs text-zinc-400 mt-1">{s.rationale}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-phase trace */}
          <div className="space-y-3">
            <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-400 flex items-center gap-2">
              <Layers className="w-4 h-4" /> Phase-by-phase trace
            </h2>
            {diffs.map((snap, idx) => (
              <PhasePanel
                key={snap.phase}
                snap={snap}
                idx={idx}
                empMap={empMap}
                shiftMap={shiftMap}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PhasePanel = ({ snap, idx, empMap, shiftMap }) => {
  const [open, setOpen] = useState(idx === 0 || snap.added.length > 0);
  const meta = PHASE_LABELS[snap.phase] || { title: snap.phase, desc: "" };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden" data-testid={`phase-${snap.phase}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-800/50 transition-colors text-left"
      >
        <ChevronRight className={`w-4 h-4 text-zinc-500 transition-transform ${open ? "rotate-90" : ""}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">{meta.title}</p>
          <p className="text-[11px] text-zinc-500 truncate">{meta.desc}</p>
        </div>
        <div className="flex items-center gap-3 text-xs tabular-nums">
          <span className="text-zinc-400">total <span className="text-white font-bold">{snap.entries_count}</span></span>
          {snap.added.length > 0 && <span className="text-emerald-400">+{snap.added.length}</span>}
          {snap.removed.length > 0 && <span className="text-rose-400">−{snap.removed.length}</span>}
        </div>
      </button>

      {open && (
        <div className="border-t border-zinc-800 p-4 space-y-4">
          {snap.added.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-emerald-400 mb-2 inline-flex items-center gap-1">
                <UsersIcon className="w-3 h-3" /> Added in this phase ({snap.added.length})
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs max-h-64 overflow-y-auto">
                {snap.added.slice(0, 100).map((e, i) => (
                  <div key={`${e.date}-${e.shift_id}-${e.employee_id}-${i}`} className="px-2 py-1 bg-emerald-950/20 border border-emerald-900/40 rounded tabular-nums flex items-center justify-between">
                    <span className="text-zinc-400">{e.date}</span>
                    <span className="text-zinc-200 truncate ml-2">{shiftMap[e.shift_id] || e.shift_id}</span>
                    <span className="text-emerald-300 ml-2 truncate">{empMap[e.employee_id] || e.employee_id}</span>
                  </div>
                ))}
                {snap.added.length > 100 && (
                  <p className="text-[10px] text-zinc-500 col-span-2">…and {snap.added.length - 100} more</p>
                )}
              </div>
            </div>
          )}
          {snap.removed.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-rose-400 mb-2">Removed (transfers, {snap.removed.length})</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs">
                {snap.removed.slice(0, 50).map((e, i) => (
                  <div key={`r-${e.date}-${e.shift_id}-${e.employee_id}-${i}`} className="px-2 py-1 bg-rose-950/20 border border-rose-900/40 rounded tabular-nums flex items-center justify-between">
                    <span className="text-zinc-400">{e.date}</span>
                    <span className="text-zinc-200 truncate ml-2">{shiftMap[e.shift_id] || e.shift_id}</span>
                    <span className="text-rose-300 ml-2 truncate">{empMap[e.employee_id] || e.employee_id}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {snap.added.length === 0 && snap.removed.length === 0 && (
            <p className="text-xs text-zinc-500 italic">This phase made no changes (state inherited from prior phase).</p>
          )}

          <div className="border-t border-zinc-800 pt-3">
            <p className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2 inline-flex items-center gap-1">
              <FlaskConical className="w-3 h-3" /> Per-employee tallies after this phase
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-1 text-[11px] tabular-nums">
              {Object.entries(snap.per_employee_counts).sort((a,b) => a[0].localeCompare(b[0])).map(([eid, cnt]) => (
                <div key={eid} className="px-2 py-1 bg-black/30 rounded flex justify-between">
                  <span className="text-zinc-400 truncate mr-1">{empMap[eid] || eid}</span>
                  <span className="text-zinc-200 font-bold">{cnt}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RosterDebugPage;
